/**
 * Арифметика нижней шторки: где она стоит, куда попадёт после броска и кому
 * достаётся жест — шторке или списку внутри неё.
 *
 * Здесь только счёт, без единого обращения к разметке. Так сделано ради
 * проверки: браузерных тестов в проекте нет, и всё, что смешано с событиями и
 * узлами, остаётся непроверенным. Компонент связывает эти правила с пальцем,
 * а решения принимаются тут.
 *
 * Три положения шторки заданы в стилях (`--sheet-shut`, `--sheet-mid`,
 * `--sheet-tall`) и приходят сюда уже в пикселях: в них есть и dvh, и
 * безопасная область телефона, а перевести это в числа умеет только браузер.
 */

/**
 * Высота видимой части шторки в каждом из трёх положений, пиксели.
 * По возрастанию: свёрнутое, среднее, раскрытое.
 */
export type Detents = readonly [number, number, number]

/** Положение шторки: 0 — свёрнута, 1 — среднее, 2 — раскрыта. */
export type Detent = 0 | 1 | 2

/** Куда идёт палец: вверх — шторка растёт, вниз — закрывается. */
export type Direction = 'up' | 'down'

/**
 * Во сколько раз панель отстаёт от пальца за крайними положениями.
 * Без отставания край ощущается обрывом, а с полной остановкой — поломкой.
 */
const RESISTANCE = 3

/**
 * Сколько времени «доезжает» брошенная панель, секунды.
 *
 * На это время вперёд и смотрит выбор положения при отпускании. Величина
 * подобрана на глаз и проверяется только рукой: 0,15 с — примерно столько
 * длится ощущение, что панель ещё летит.
 */
const GLIDE_SECONDS = 0.15

/**
 * Какому положению соответствует видимая часть высотой `shown`.
 *
 * Граница проходит ровно посередине между соседними положениями. На самой
 * границе выбирается меньшее — не потому, что так правильнее, а потому, что
 * ответ обязан быть определённым: иначе на границе значение прыгало бы от
 * дробных долей пикселя, а на нём висят стили панели.
 */
export function detentFor(shown: number, detents: Detents): Detent {
  const [shut, mid, tall] = detents
  if (shown <= (shut + mid) / 2) return 0
  if (shown <= (mid + tall) / 2) return 1
  return 2
}

/**
 * Насколько панель сдвинется, если палец ушёл за крайнее положение на `over`.
 *
 * Всегда меньше поданного и в ту же сторону: панель идёт за пальцем, но
 * заметно отстаёт — так видно, что дальше некуда, и при этом ничего не
 * упирается намертво.
 */
export function resist(over: number, factor: number = RESISTANCE): number {
  return over / factor
}

/** Видимая часть с учётом сопротивления за пределами крайних положений. */
export function withResistance(shown: number, detents: Detents): number {
  const [shut, , tall] = detents
  if (shown > tall) return tall + resist(shown - tall)
  if (shown < shut) return shut - resist(shut - shown)
  return shown
}

/**
 * Куда снапить панель, отпущенную на высоте `shown` со скоростью `velocity`
 * (пикселей в секунду, положительная — шторка растёт).
 *
 * Считается по спроецированному положению, а не по одному расстоянию: короткий
 * быстрый бросок вверх должен раскрывать шторку, даже если палец прошёл треть
 * пути. Мерить только расстояние — значит не заметить броска и вернуть панель
 * туда, откуда её только что кинули.
 */
export function projectRelease(
  shown: number,
  velocity: number,
  detents: Detents,
): Detent {
  return detentFor(shown + velocity * GLIDE_SECONDS, detents)
}

/**
 * Кому достаётся жест: шторке (`true`) или списку внутри неё (`false`).
 *
 * То же правило, что в системных шторках телефона. Решение принимается один
 * раз в начале жеста и до конца его не меняется: иначе палец перескакивал бы
 * между шторкой и списком на середине движения.
 */
export function shouldDragSheet(
  detent: Detent,
  direction: Direction,
  listTop: number,
): boolean {
  // Пока шторка не раскрыта, список ей не соперник: иначе её нечем поднять.
  if (detent !== 2) return true
  // Раскрыта — список свой, и отбираем мы у него движение только на пути вниз
  // от самого верха: с этого и начинается закрытие.
  return direction === 'down' && listTop <= 0
}

/** Следующее положение по кругу — по нему ходит нажатие на ручку. */
export function nextDetent(detent: Detent): Detent {
  return (((detent + 1) % 3) as Detent)
}
